import { Box, Button, Flex, Image, Stack, Text, useToast } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { SessionDto } from "@bcasmart/doxecure-commons/doxecureClientTypes";
import { ContactToValidateBody, SessionVersion, TokenSessionStructure } from "@bcasmart/doxecure-commons/doxecureTypes";
import { $count, $email, $ok, $value } from "foundation-ts/commons";
import { TSDate } from "foundation-ts/tsdate";
import { Nullable, UUID } from "foundation-ts/types";

import { NewPersonFormValues } from "@/components/ContactForm/ContactForm";
import { G1Button } from "@components/G1Button";
import { TemporaryChildProps } from "@components/layouts/TemporaryAuthenticatedLayout";
import { AsyncRefuseModal } from "@components/Modals/AsyncRefuseModal";
import { AsyncValidateContactInfosModal } from "@components/Modals/AsyncValidateContactInfosModal";
import { DefaultWaitingModalState, DefaultAsyncRefuseModalState, DefaultAsyncValidateContactInfosModalState } from "@components/Modals/ModalConstants";
import { WaitingModal } from "@/components/Modals/WaitingModal";
import { config } from "@/config/config";
import { DocumentsViewer } from "@containers/generics/DocumentsViewer";
import { useDevice } from "@hooks/useDevice";
import { deleteOneSessionForUser, refuseSignature, updateSessionContact } from "@/services/session.service";
import { $g1IconURL } from "@/theme/G1Style";
import { G1Icon } from "@/utils/icons";
import { localtrans } from "@/utils/LocalTranslations";
import { defaultErrorToast, defaultSuccessToast } from "@/utils/toast";

export interface SessionSharedData extends TokenSessionStructure {
    session?: Nullable<SessionDto>;
    token?: Nullable<string>;
}

export const ExternalSignView = ({ sharedData } : TemporaryChildProps) => {
    const { isMOBILE } = useDevice();
    const [selectedSession, setSelectedSession] = useState<Nullable<SessionDto>>(null);
    const [profileId, setProfileId] = useState<Nullable<UUID>>(null);
    const [token, setToken] = useState<Nullable<string>>(null);
    const [senderName, setSenderName] = useState("");
    const [message, setMessage] = useState("");
    const [signer, setSigner] = useState<Nullable<ContactToValidateBody>>(null);
    const [infoPanelOpen, setInfoPanelOpen] = useState(true);
    const [validationModal, setValidationModal] = useState(DefaultAsyncValidateContactInfosModalState);
    const [refusingModal, setRefusingModal] = useState(DefaultAsyncRefuseModalState);
    const [waitingModal, setWaitingModal] = useState(DefaultWaitingModalState);
    const navigate = useNavigate();
    const toast = useToast();

    const showValidationModal = (contactInfos: ContactToValidateBody, senderName: string, freshData?: SessionSharedData) => {
        setValidationModal({
            title: "Validation de vos informations",
            senderName,
            contactInfos,
            isOpen: true,
            size: "xl",
            onAccept: async (updatedInfos, badRecipient, notusingPhonenumber) => {
                const data = freshData ?? { session: selectedSession, profileId, token, sessionId: selectedSession?.apid } as SessionSharedData;
                await onPatchSessionContact(data, updatedInfos, badRecipient, notusingPhonenumber);
            },
            onClose: async () => setValidationModal(DefaultAsyncValidateContactInfosModalState)
        });
    }

   const onRefuse = async (sessionSharedData: SessionSharedData, reason: string) => {
        setWaitingModal({ message: localtrans('WaitingForSignatureRefused'), isOpen: true, onClose: () => setWaitingModal(DefaultWaitingModalState) });
        const signatureId = sessionSharedData.session?.signatures?.filter(sign => sign.signer.apid === sessionSharedData.profileId).first()?.apid;
        const done = await refuseSignature(sessionSharedData.sessionId, signatureId, sessionSharedData.profileId, TSDate.zulu(), reason, sessionSharedData.token);
        setWaitingModal(DefaultWaitingModalState);
        if (!!done) {
            toast(defaultSuccessToast(localtrans('DocumentsViewer.SignatureWasRefused')));
            try {
                await deleteOneSessionForUser(sessionSharedData.sessionId, sessionSharedData.token);
                navigate('/result-external-sign?ret=0');
            } catch (e) {
               toast(defaultErrorToast("Erreur lors de l'expiration de la session de signature"));
            }
        }
        else {
            toast(defaultErrorToast(localtrans('DocumentsViewer.SignatureCouldNotBeRefused'))) ; 
        }
    }

    const onPatchSessionContact = async (sessionSharedData: SessionSharedData, updatedInfos: NewPersonFormValues, badRecipient?: boolean, notusingPhonenumber?: boolean) => {
        try { 
            const updated = await updateSessionContact(sessionSharedData.sessionId, sessionSharedData.profileId, { 
                email: $email(updatedInfos.email)!,
                lastName: updatedInfos.lastName.toUpperCase(),
                firstName: updatedInfos.firstName?.capitalize(),
                phone: updatedInfos.phone,
                status: !!badRecipient ? "badRecipient" : undefined,
                notusingPhonenumber
            }, sessionSharedData.token!);
            if (updated) {
                toast(defaultSuccessToast("Les données ont bien été prises en compte"));
                setSigner({
                    email: $email(updatedInfos.email)!,
                    lastName: updatedInfos.lastName.toUpperCase(),
                    firstName: updatedInfos.firstName?.capitalize(),
                    phone: updatedInfos.phone,
                });
            }
            if (!!badRecipient) {
                setRefusingModal({
                    title: "Refus de signer",
                    defaultReason: "Je ne suis pas le bon destinataire",
                    isOpen: true,
                    onAccept: async (reason: string) => await onRefuse(sessionSharedData, reason),
                    onClose: async () => setRefusingModal(DefaultAsyncRefuseModalState)
                });
            }
        } catch (e) {
            toast(defaultErrorToast("Erreur lors de la mise à jour de vos données"));
        }
        setValidationModal(DefaultAsyncValidateContactInfosModalState);
    }

    useEffect(() => {
        if ($ok(sharedData)) {    
            const sessionSharedData = sharedData as SessionSharedData;
            const session = sessionSharedData.session;
            setSelectedSession(session);
            setProfileId(sessionSharedData.profileId);
            setToken(sessionSharedData.token);
            const sender = $ok(session?.expeditor) ? `${session!.expeditor!.firstName} ${session!.expeditor!.lastName.toUpperCase()}` : "Inconnu";
            setSenderName(sender);
            const msg =  `${sender} vous a transmis ${$count(session?.documents)} document${$count(session?.documents) > 1 ? "s" : ""} à signer avant le ${$value(session?.validity?.end?.toLocaleString("%d/%m/%Y"), '')}${session?.version === SessionVersion.FirstVersion ? ' avec votre clé de signature' : ''}.`;
            setMessage(msg);
            if ($ok(sessionSharedData.contactInfosToValidate)) {
                setSigner(sessionSharedData.contactInfosToValidate);
                showValidationModal(sessionSharedData.contactInfosToValidate!, sender, sessionSharedData);
            } else {
                const signer = session?.signatures?.filter(sign => sign.signer.apid === sessionSharedData.profileId).first()?.signer;
                setSigner({ email: signer!.email, lastName: signer!.lastName, firstName: signer!.firstName, phone: signer!.phone });
            }
        }
    }, [sharedData]);

    return (
        <>
            <Flex flexDirection="column" h="full" w="full">
                <Flex alignItems="center" justifyContent="space-between" px="4" py="2" borderBottomWidth="0.5px">
                    <Stack direction="column" alignItems="center" spacing="1">
                        <Image src={$g1IconURL("sidebar.logo.iconurl")} alt="Application logo" boxSize="32px" />
                        {!isMOBILE && <Text fontWeight="500" fontSize="sm">{config.app.title}</Text>}
                    </Stack>
                    <Text fontSize={isMOBILE ? "sm" : "lg"} mb="2" ml={2}>{message}{isMOBILE ? <br/> : " "}Scrollez jusqu'à la dernière page de chaque document ci-dessous afin de pouvoir les signer.</Text>
                    <Button variant="ghost" size="sm" rightIcon={infoPanelOpen ? <G1Icon.Up /> : <G1Icon.Down />} onClick={() => setInfoPanelOpen(!infoPanelOpen)}>{isMOBILE ? "" : (infoPanelOpen ? "Masquer les détails" : "Détails")}</Button>
                </Flex>

                {infoPanelOpen && $ok(signer) && (
                    <Box bg="surface.2" borderWidth="0.5px" borderRadius="md" p="2" m="2">
                        <Flex alignItems="center" mt="2" gap={2}>
                            <Text fontSize={isMOBILE ? "sm" : "lg"}>Vous avez la possibilité de mettre à jour certaines de vos données : {signer?.lastName}{$ok(signer?.firstName) ? ` ${signer?.firstName}` : ""}{$ok(signer?.phone) ? ` (${signer?.phone})` : ""}</Text>
                            <G1Button component="global" variant="ok" size="sm" title="Mettre à jour" fontSize={isMOBILE ? "xs" : "lg"} onClick={() => showValidationModal(signer!, senderName)} />
                        </Flex>
                    </Box>
                )}

                <Flex flexDirection="column" overflow="auto" flex="1" px="4" mt={1}>
                    <DocumentsViewer
                        sessionId={selectedSession?.apid}
                        profileId={profileId}
                        acceptsSignature={true}
                        validityDate={$value(selectedSession?.validity?.end?.toLocaleString("%d/%m/%Y"), '')}
                        immutableDocuments={true}
                        senderName={senderName}
                        signatureMode={selectedSession?.signatures?.first()?.signatureMode}
                        token={token}
                    />
                </Flex>
            </Flex>
            <WaitingModal message={waitingModal.message} isOpen={waitingModal.isOpen} onClose={waitingModal.onClose} />
            <AsyncValidateContactInfosModal 
                title={validationModal.title}
                isOpen={validationModal.isOpen}
                size={validationModal.size}
                senderName={validationModal.senderName}
                contactInfos={validationModal.contactInfos}
                onAccept={validationModal.onAccept}
                onClose={validationModal.onClose} />
            <AsyncRefuseModal 
                title={refusingModal.title}
                defaultReason={refusingModal.defaultReason}
                isOpen={refusingModal.isOpen}
                onAccept={refusingModal.onAccept}
                onClose={refusingModal.onClose} />
        </>
    );
};